export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: "/auth/login",
    REGISTER: "/auth/register",
    REFRESH: "/auth/refresh-token",
    LOGOUT: "/auth/logout",
    PROFILE: "/auth/profile",
    FORGOT_PASSWORD: "/auth/forget-password",
    SETUP_PASSWORD: "/auth/setup-password",
    CHANGE_PASSWORD: "/auth/change-password",
    SWITCH: (companyId: string | number) => `/auth/switch/${companyId}`,
  },
  USERS: {
    BASE: "/user",
    BY_ID: (id: string) => `/user/${id}`,
    CREATE: "/user",
    UPDATE: (id: string) => `/user/${id}`,
    DELETE: (id: string) => `/user/${id}`,
    REACTIVATE: (id: string) => `/user/${id}/re-activate`,
    CHANGE_AVATAR: (id: string) => `/user/${id}/change-avatar`,
    DROPDOWN: "/user/dropdown",
    RESET_PASSWORD: (id: string) => `/user/reset-password/${id}`,
    ACTIVITIES: (id: string | number) => `/user/${id}/activities`,
    SCHEDULE_TASKS: (id: string | number) => `/user/${id}/schedule-task`,
  },
  PRODUCTS: {
    BASE: "/products",
    BY_ID: (id: string) => `/products/${id}`,
    CREATE: "/products",
    UPDATE: (id: string) => `/products/${id}`,
    DELETE: (id: string) => `/products/${id}`,
  },
  CATEGORIES: {
    BASE: "/categories",
    BY_ID: (id: string) => `/categories/${id}`,
    CREATE: "/categories",
    UPDATE: (id: string) => `/categories/${id}`,
    DELETE: (id: string) => `/categories/${id}`,
  },
  ROLES: {
    BASE: "/role",
    BY_ID: (id: string) => `/role/${id}`,
    CREATE: "/role",
    UPDATE: (id: string) => `/role/${id}`,
    DELETE: (id: string) => `/role/${id}`,
    REACTIVATE: (id: string) => `/role/${id}/re-activate`,
    DROPDOWN: (companyId: string | number) => `/role/dropdown/${companyId}`,
    ASSIGN_USER: (roleId: string | number) => `/role/${roleId}/user`,
  },
  PERMISSIONS: {
    BASE: "/permissions",
  },
  FILES: {
    REQUEST_UPLOAD: "/files/request-upload",
    COMMIT_UPLOAD: "/files/commit-upload",
    DOWNLOAD: (id: string | number) => `/files/download/${id}`,
  },
  COMPANY: {
    BASE: "/company",
    BY_ID: (id: string) => `/company/${id}`,
    UPDATE: (id: string) => `/company/${id}`,
    DROPDOWN: "/company/dropdown",
    REACTIVATE: (id: string) => `/company/${id}/re-activate`,
  },
  CLIENT: {
    BASE: "/client",
    BY_ID: (id: string | number) => `/client/${id}`,
    DROPDOWN: "/client/dropdown",
  },
  PROVINCE: {
    DROPDOWN: "/province/dropdown",
  },
  UNITS: {
    BASE: "/unit",
    BY_ID: (id: string) => `/unit/${id}`,
    CREATE: "/unit",
    UPDATE: (id: string | number) => `/unit/${id}`,
    DELETE: (id: string | number) => `/unit/${id}`,
    DISCARD: (id: string | number) => `/unit/${id}/discard`,
    FACILITY_PATH: "/unit/facility-path",
    DROPDOWN: (type?: string) => (type ? `/unit/dropdown/${type}` : "/unit/dropdown"),
    DROPDOWN_UNUSED: (type?: string) => (type ? `/unit/dropdown/${type}/unused` : "/unit/dropdown/unused"),
    DROPDOWN_WITH_KEYS: "/unit-go/dropdown",
    CURRENT_OCCUPANCY: (id: string | number) => `/unit/${id}/current-occupancy`,
    HISTORY_OCCUPANCY: (id: string | number) => `/unit/${id}/history-occupancy`,
  },
  PIT_TAGS: {
    BASE: "/pit-tag",
    BY_ID: (id: string | number) => `/pit-tag/${id}`,
    CREATE: "/pit-tag",
    DELETE: (id: string | number) => `/pit-tag/${id}`,
    RELEASE: (id: string | number) => `/pit-tag/${id}/release`,
    IMPORT_DATA: "/pit-tag/import",
    IMPORT_TEMPLATE: "/pit-tag/import-template",
    UNUSED: "/pit-tag/unused",
    CURRENT_OCCUPANCY: (id: string | number) => `/pit-tag/${id}/current-occupancy`,
    HISTORY_OCCUPANCY: (id: string | number) => `/pit-tag/${id}/history-occupancy`,
  },
  PROGRAMS: {
    BASE: "/program",
    BY_ID: (id: string | number) => `/program/${id}`,
    CREATE: "/program",
    UPDATE: (id: string | number) => `/program/${id}`,
    SELECTED_BROODSTOCK: (programId: string | number) => `/program/${programId}/selected-broodstock`,
    SUMMARY_SELECTED_BROODSTOCK: (programId: string | number) => `/program/${programId}/summary-selected-broodstock`,
    SUMMARY_BROODSTOCK: (programId: string | number) => `/program/${programId}/summary-broodstock`,
    AVAILABLE_BROODSTOCK: "/program/available-broodstock",
    UNASSIGNED_BROODSTOCK: (programId: string | number) => `/program/${programId}/unassign-broodstock`,
    ASSIGNED_BROODSTOCK: (programId: string | number, unitId: string | number) => `/program/${programId}/assigned-broodstock/${unitId}`,
    ASSIGN_BROODSTOCK: (programId: string | number, unitId: string | number) => `/program/${programId}/assign-broodstock/${unitId}`,
    ADD_BROODSTOCK_TO_PROGRAM: (programId: string | number) => `/program/${programId}/assign-broodstock`,
    REMOVE_BROODSTOCK: (programId: string | number) => `/program/${programId}/selected-broodstock`,
    REMOVE_ASSIGNED_BROODSTOCK: (programId: string | number) => `/program/${programId}/remove-broodstock`,
    IMPORT_BROODSTOCK: (programId: string | number) => `/program/${programId}/import-broodstock`,
    IMPORT_TEMPLATE: "/program/import-template",
    CREATE_BROODSTOCK: (programId: string | number) => `/program/${programId}/broodstock`,
    UPDATE_BROODSTOCK: (programId: string | number, broodstockId: string | number) => `/program/${programId}/broodstock/${broodstockId}`,
    BROODSTOCK_BY_ID: (programId: string | number, broodstockId: string | number) => `/program/${programId}/broodstock/${broodstockId}`,
    MATURATION_TANK: (programId: string | number) => `/program/${programId}/maturation-tank`,
    ELITE_MATURATION_TANK: (programId: string | number) => `/program/${programId}/elite/maturation-tank`,
    ELITE_AVAILABLE_BROODSTOCK: (programId: string | number) => `/program/${programId}/elite/available-broodstock`,
    ELITE_ASSIGNED_BROODSTOCK: (programId: string | number, unitId: string | number) => `/program/${programId}/elite/assigned-broodstock/${unitId}`,
    ASSIGN_ELITE_BROODSTOCK: (programId: string | number, unitId: string | number) => `/program/${programId}/elite/assign-broodstock/${unitId}`,
    REMOVE_ELITE_ASSIGNED_BROODSTOCK: (programId: string | number, unitId: string | number) => `/program/${programId}/elite/remove-broodstock/${unitId}`,
    DISCARD: (programId: string | number, unitId: string | number) => `/program/${programId}/discard/${unitId}`,
    UPDATE_PHENOTYPE: (programId: string | number, unitId: string | number) => `/program/${programId}/phenotype/${unitId}`,
    UPDATE_ELITE_PHENOTYPE: (programId: string | number, unitId: string | number) => `/program/${programId}/elite/phenotype/${unitId}`,
    FINALIZE_PARENT_BROODSTOCK: (programId: string | number) => `/program/${programId}/elite/finalized-parent-selections`,
    FINALIZE_ELITE_TRANSFER: (programId: string | number) => `/program/${programId}/finalized-elite-selections`,
    SUMMARY_ELITE_BROODSTOCK: (programId: string | number) => `/program/${programId}/elite/summary-broodstock`,
    OVERVIEW: (id: string | number) => `/${id}/overview`,
  },
  MATURATION: {
    MATING_FREQUENCY: "/maturation/mating-freq",
    MATING_FREQUENCY_DETAIL: (id: string | number) => `/maturation/mating-freq/${id}`,
    MATING_HISTORY: (id: string | number) => `/maturation/mating-freq/${id}/history`,
    GLOBAL_MATING_HISTORY: "/maturation/mating-freq/history",
    GLOBAL_MATING_HISTORY_EXPORT: "/maturation/mating-freq/history/export",
    ADD_MATING: (id: string | number) => `/maturation/mating-freq/${id}`,
    BULK_ADD_MATING: "/maturation/mating-freq/bulk",
    UPDATE_MATING: (id: string | number) => `/maturation/mating-freq/history/${id}`,
    DELETE_MATING: (id: string | number) => `/maturation/mating-freq/history/${id}`,
    MORTALITY: "/maturation/mortality",
    ADD_MORTALITY: (id: string | number) => `/maturation/mortality/${id}`,
    ANONYMOUS_MORTALITY: "/maturation/anonim-mortality",
    MORTALITY_LIST: "/maturation/mortality/list",
    WEIGHT_BIOMASS: "/weight-n-biomass",
    WEIGHT_BIOMASS_DETAIL: (id: string | number) => `/weight-n-biomass/${id}`,
    WEIGHT_BIOMASS_OBSERVATION_DETAIL: (id: string | number) => `/weight-n-biomass/detail-observation/${id}`,
    SUBMIT_WEIGHT_BIOMASS_OBSERVATION: (id: string | number) => `/weight-n-biomass/submit-observation/${id}`,
    WEIGHT_BIOMASS_PRE_OBSERVATION: (programId: string | number) => `/weight-n-biomass/pre-observation/${programId}`,
    CALCULATE_BIOMASS: "/weight-n-biomass/calculate-biomass",
    WEIGHT_BIOMASS_OBSERVATION_EXPORT: (id: string | number) => `/weight-n-biomass/detail-observation/${id}/excel`,
    FEEDING_ENTRY: "/feeding-entry",
    FEEDING_ENTRY_DETAIL: (id: string | number) => `/feeding-entry/${id}/detail`,
  },
  BATCHES: {
    BASE: "/batch",
    BY_ID: (id: string | number) => `/batch/${id}`,
    PRE_HATCHING: (batchId: string | number) => `/batch/pre-hatching/${batchId}`,
    AVAILABLE_PRE_HATCHING: (batchId: string | number) => `/batch/pre-hatching/${batchId}/available`,
    ADD_PRE_HATCHING: (batchId: string | number) => `/batch/pre-hatching/${batchId}/add`,
    FINALIZE_PRE_HATCHING: (batchId: string | number) => `/batch/pre-hatching/${batchId}/finalize`,
    REMOVE_PRE_HATCHING: (batchId: string | number) => `/batch/pre-hatching/${batchId}/remove`,
    UPDATE_PRE_HATCHING_INDEX: (id: string | number) => `/batch/pre-hatching/${id}/update-index`,
    HATCHING: (batchId: string | number) => `/hatching/${batchId}`,
    HATCHING_BROODSTOCK_DETAIL: (id: string | number) => `/hatching/broodstock/${id}`,
    DISCARD_HATCHING_BROODSTOCK: (id: string | number) => `/hatching/broodstock/${id}`,
    EXPORT_HATCHING_EXCEL: (batchId: string | number) => `/hatching/${batchId}/export-excel`,
    PRE_SELECTION: (batchId: string | number) => `/hatching/${batchId}/pre-selection`,
    ADD_HATCHING: (batchId: string | number) => `/hatching/${batchId}`,
    HATCHING_FACTOR: "/hatching/factor/harvest",
    HARVEST_FACTOR: "/hatching/factor/harvest",
    LEFTOVER_FACTOR: "/hatching/factor/leftover",
    EGG_FACTOR: "/hatching/factor/egg",
    ADD_HARVEST: (id: string | number) => `/hatching/broodstock/${id}/harvest`,
    UPDATE_EGGS_FERTIL: (id: string | number) => `/hatching/broodstock/${id}/spawn-information`,
    CALCULATE_EGG: "/hatching/calculate-egg",
    ADD_STOCK_TO_LARVAE: (id: string | number) => `/hatching/broodstock/${id}/stock-to-larvae`,
    HATCHING_BROODSTOCK_VALIDATION: (id: string | number) => `/hatching/broodstock/${id}/validation`,
    LARVAE: (batchId: string | number) => `/larvae/${batchId}`,
    LARVIA: (batchId: string | number) => `/larvae/${batchId}/larvia`,
    EXPORT_LARVAE_EXCEL: (batchId: string | number) => `/larvae/${batchId}/export`,
    EXPORT_LARVIA_EXCEL: (batchId: string | number) => `/larvae/${batchId}/larvia/export`,
    TRANSFER_LARVAE_TANK: (id: string | number) => `/larvae/${id}/transfer-tank`,
    LARVAE_DETAIL: (id: string | number) => `/larvae/${id}/detail`,
    LARVAE_MONITORING: (id: string | number) => `/larvae/${id}/detail/monitoring`,
    LARVAE_MONITORING_POST: (id: string | number) => `/larvae/${id}/monitoring`,
    LARVAE_MONITORING_PL4_POST: (id: string | number) => `/larvae/${id}/monitoring-pl4`,
    LARVAE_SAMPLING_POPULATION: (id: string | number) => `/larvae/${id}/detail/sampling-population`,
    LARVAE_TEMPERATURE: (id: string | number) => `/larvae/${id}/detail/temperature`,
    LARVAE_TEMPERATURE_POST: (id: string | number) => `/larvae/${id}/temperature`,
    LARVAE_TEMPERATURE_DETAIL: (temperatureId: string | number) => `/larvae/temperature/${temperatureId}`,
    LARVAE_TEMPERATURE_UPDATE: (temperatureId: string | number) => `/larvae/temperature/${temperatureId}`,
    LARVAE_LARVIA_DETAIL: (id: string | number) => `/larvae/${id}/detail/larvia`,
    CALCULATE_MANUAL_MEASUREMENT: `/larvae/calculator/manual-measurement`,
    CALCULATE_LARVIA_MEASUREMENT: `/larvae/calculator/larvia-measurement`,
    CALCULATE_WEIGHT_PRODUCTION: `/larvae/calculator/weight-production`,
    LARVAE_MONITORING_EXPORT: (id: string | number) => `/larvae/${id}/detail/monitoring/export`,
    LARVAE_MONITORING_DETAIL: (monitoringId: string | number) => `/larvae/monitoring/${monitoringId}`,
    LARVAE_MONITORING_UPDATE: (monitoringId: string | number) => `/larvae/monitoring/${monitoringId}`,
    LARVAE_SAMPLING_POPULATION_EXPORT: (id: string | number) => `/larvae/${id}/detail/sampling-population/export`,
    LARVAE_TEMPERATURE_EXPORT: (id: string | number) => `/larvae/${id}/detail/temperature/export`,
    FLUSH: (id: string | number) => `/larvae/${id}/flush`,
    DISCARD: (id: string | number) => `/larvae/${id}/discard`,
    STOCK_TO_GO: (id: string | number) => `/larvae/${id}/stock-to-go`,
    STOCK_TO_GROWOUT: (id: string | number) => `/larvae/${id}/stock-to-growout`,
    STOCK_TO_PRODUCTION: (id: string | number) => `/larvae/${id}/stock-to-production`,
    OVERVIEW: (id: string | number) => `/batch/${id}/overview`,
  },
  GROWOUT: {
    BASE: "/growout",
    METABASE_20_GR_SAMPLE_SUBMISSION_DASHBOARD: "/metabase/dashboard/20-gr-sample-submission",
    DISCARD: (id: string | number) => `/growout/${id}/discard`,
    TRANSFER: (id: string | number) => `/growout/${id}/transfer`,
    STOCKING: (batchId: string | number) => `/growout/${batchId}/stocking`,
    EXPORT_STOCKING: (batchId: string | number) => `/growout/${batchId}/stocking/export`,
    WEIGHT_SAMPLING: (batchId: string | number) => `/growout/${batchId}/weight-sampling`,
    EXPORT_WEIGHT_SAMPLING: (batchId: string | number) => `/growout/${batchId}/weight-sampling/export`,
    CALCULATE_WEIGHT_SAMPLING: (batchId: string | number, id: string | number) => `/growout/${batchId}/weight-sampling/${id}/weight-sampling/calculate`,
    ADD_WEIGHT_SAMPLING: (batchId: string | number, id: string | number) => `/growout/${batchId}/weight-sampling/${id}/weight-sampling`,
    WEIGHT_SAMPLING_DETAIL: (batchId: string | number, id: string | number) => `/growout/${batchId}/weight-sampling/${id}`,
    UPDATE_WEIGHT_SAMPLING: (batchId: string | number, id: string | number, samplingId: string | number) => `/growout/${batchId}/weight-sampling/${id}/sampling/${samplingId}`,
    EXPORT_WEIGHT_SAMPLING_DETAIL: (batchId: string | number, id: string | number) => `/growout/${batchId}/weight-sampling/${id}/export`,
    TRANSFER_WEIGHT_SAMPLING: (batchId: string | number, id: string | number) => `/growout/${batchId}/weight-sampling/${id}/transfer`,
    DETAIL: (batchId: string | number, id: string | number) => `/growout/${batchId}/${id}/detail`,
    CALCULATE_3GR_SELECTION: (batchId: string | number, id: string | number) => `/growout/${batchId}/weight-sampling/${id}/3-gr-selection/calculate`,
    ADD_3GR_SELECTION: (batchId: string | number, id: string | number) => `/growout/${batchId}/weight-sampling/${id}/3-gr-selection`,
    THREE_GRAM: (batchId: string | number) => `/growout/${batchId}/3-gram`,
    EXPORT_3_GRAM: (batchId: string | number) => `/growout/${batchId}/3-gram/export`,
    DISCARD_POST_LARVAE: (batchId: string | number, id: string | number) => `/growout/${batchId}/weight-sampling/${id}/discard`,
    CALCULATE_20GR_SELECTION: (batchId: string | number, id: string | number) => `/growout/${batchId}/weight-sampling/${id}/20-gr-selection/calculate`,
    ADD_20GR_SELECTION: (batchId: string | number, id: string | number) => `/growout/${batchId}/weight-sampling/${id}/20-gr-selection`,
    TWENTY_GRAM: (batchId: string | number) => `/growout/${batchId}/20-gram`,
    EXPORT_TWENTY_GRAM: (batchId: string | number) => `/growout/${batchId}/20-gram/export`,
    RECORD_20GR_SAMPLING: (batchId: string | number, id: string | number) => `/growout/${batchId}/20-gram/${id}/record-sampling`,
    RECORD_3GR_SAMPLING: (batchId: string | number, id: string | number) => `/growout/${batchId}/3-gram/${id}/record-sampling`,
    THREE_GRAM_SAMPLING_DETAIL: (batchId: string | number, id: string | number) => `/growout/${batchId}/3-gram/${id}`,
    TWENTY_GRAM_SAMPLING_DETAIL: (batchId: string | number, id: string | number) => `/growout/${batchId}/20-gram/${id}`,
    EXPORT_3_GRAM_SAMPLING_RECORD: (batchId: string | number, id: string | number) => `/growout/${batchId}/3-gram/${id}/export`,
    EXPORT_20_GRAM_SAMPLING_RECORD: (batchId: string | number, id: string | number) => `/growout/${batchId}/20-gram/${id}/export`,
    ADD_OTHER_GENOTYPING: (batchId: string | number, growoutIndividualId: string | number) => `/growout/${batchId}/other-genotyping/${growoutIndividualId}`,
    OTHER_GENOTYPING_DATA: (batchId: string | number, growoutIndividualId: string | number) => `/growout/${batchId}/other-genotyping/${growoutIndividualId}/data`,
    OTHER_GENOTYPING_DETAIL: (batchId: string | number, growoutIndividualId: string | number) => `/growout/${batchId}/other-genotyping/${growoutIndividualId}/detail`,
    EXPORT_OTHER_GENOTYPING_DATA: (batchId: string | number, growoutIndividualId: string | number) => `/growout/${batchId}/other-genotyping/${growoutIndividualId}/data/export`,
    UPDATE_OTHER_GENOTYPING: (batchId: string | number, growoutIndividualId: string | number, id: string | number) => `/growout/${batchId}/other-genotyping/${growoutIndividualId}/${id}`,
    DELETE_OTHER_GENOTYPING: (batchId: string | number, growoutIndividualId: string | number, id: string | number) => `/growout/${batchId}/other-genotyping/${growoutIndividualId}/${id}`,
    UPDATE_20GR_SELECTION: (batchId: string | number, id: string | number) => `/growout/${batchId}/weight-sampling/${id}/20-gr-selection`,
    INFORMATION_20_GRAM: (batchId: string | number, id: string | number) => `/growout/${batchId}/20-gram/${id}/information`,
    DETAIL_20GR_SELECTION: (batchId: string | number, id: string | number) => `/growout/${batchId}/weight-sampling/${id}/20-gr-selection`,
  },
  GROWOUT_MIX: {
    WEIGHT_SAMPLING: (batchId: string | number) => `/growout-mix/${batchId}/weight-sampling`,
    EXPORT_WEIGHT_SAMPLING: (batchId: string | number) => `/growout-mix/${batchId}/weight-sampling/export`,
    WEIGHT_SAMPLING_DETAIL: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/weight-sampling/${id}`,
    EXPORT_WEIGHT_SAMPLING_DETAIL: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/weight-sampling/${id}/export`,
    UPDATE_WEIGHT_SAMPLING: (batchId: string | number, id: string | number, samplingId: string | number) => `/growout-mix/${batchId}/weight-sampling/${id}/sampling/${samplingId}`,
    CALCULATE_WEIGHT_SAMPLING: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/weight-sampling/${id}/weight-sampling/calculate`,
    ADD_WEIGHT_SAMPLING: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/weight-sampling/${id}/weight-sampling`,
    DISCARD_POST_LARVAE: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/weight-sampling/${id}/discard`,
    TRANSFER: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/weight-sampling/${id}/transfer`,
    CALCULATE_3GR_SELECTION: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/weight-sampling/${id}/3-gr-selection/calculate`,
    ADD_3GR_SELECTION: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/weight-sampling/${id}/3-gr-selection`,
    CALCULATE_20GR_SELECTION: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/weight-sampling/${id}/20-gr-selection/calculate`,
    ADD_20GR_SELECTION: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/weight-sampling/${id}/20-gr-selection`,
    THREE_GRAM: (batchId: string | number) => `/growout-mix/${batchId}/3-gram`,
    THREE_GRAM_SAMPLING_DETAIL: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/3-gram/${id}`,
    RECORD_3GR_SAMPLING: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/3-gram/${id}/record-sampling`,
    EXPORT_3_GRAM: (batchId: string | number) => `/growout-mix/${batchId}/3-gram/export`,
    TWENTY_GRAM: (batchId: string | number) => `/growout-mix/${batchId}/20-gram`,
    RECORD_20GR_SAMPLING: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/20-gram/${id}/record-sampling`,
    TWENTY_GRAM_SAMPLING_DETAIL: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/20-gram/${id}`,
    EXPORT_TWENTY_GRAM: (batchId: string | number) => `/growout-mix/${batchId}/20-gram/export`,
    EXPORT_20_GRAM: (batchId: string | number) => `/growout-mix/${batchId}/20-gram/export`,
    DETAIL: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/${id}/detail`,
    EXPORT_3_GRAM_SAMPLING_RECORD: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/3-gram/${id}/export`,
    EXPORT_20_GRAM_SAMPLING_RECORD: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/20-gram/${id}/export`,
    UPDATE_20GR_SELECTION: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/weight-sampling/${id}/20-gr-selection`,
    INFORMATION_20_GRAM: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/20-gram/${id}/information`,
    DETAIL_20GR_SELECTION: (batchId: string | number, id: string | number) => `/growout-mix/${batchId}/weight-sampling/${id}/20-gr-selection`,
  },
  FACILITY: {
    BASE: "/facility",
    BY_ID: (id: string | number) => `/facility/${id}`,
  },
  FAMILY: {
    DROPDOWN: "/family",
  },
  GENETICS_AND_BROODSTOCK: {
    BASE: "/genetics-and-broodstock",
    SAMPLING_20_GR_LIST: "/genetics-and-broodstock/20-gr-sampling",
    BY_ID: (id: string | number) => `/genetics-and-broodstock/${id}`,
    EXPORT: (id: string | number) => `/genetics-and-broodstock/${id}/export`,
    DATA: (id: string | number) => `/genetics-and-broodstock/${id}/data`,
    TAG_DETAIL: (id: string | number, detailId: string | number) => `/genetics-and-broodstock/${id}/detail/${detailId}`,
    TAG_DATA: (id: string | number, detailId: string | number) => `/genetics-and-broodstock/${id}/detail/${detailId}/data`,
    TAG_DATA_EXPORT: (id: string | number, detailId: string | number) => `/genetics-and-broodstock/${id}/detail/${detailId}/data/export`,
    DELETE_TAG: (id: string | number, detailId: string | number, dataId: string | number) => 
      `/genetics-and-broodstock/${id}/detail/${detailId}/${dataId}`,
    UPDATE_TAG: (id: string | number, detailId: string | number, dataId: string | number) =>
      `/genetics-and-broodstock/${id}/detail/${detailId}/${dataId}`,
    DISCARD: "/genetics-and-broodstock/discard",
    TRANSFER_TO_PROD: "/genetics-and-broodstock/transfer-to-prod",
    TRANSFER_TO_GO_GENETIC: "/genetics-and-broodstock/transfer-to-go/genetic",
    TRANSFER_TO_GO_BROODSTOCK: "/genetics-and-broodstock/transfer-to-go/broodstock",
    SAMPLING_20_GR: (growoutPreIndividualId: string | number) =>
      `/genetic-20-gr-sampling/growout-pre-individual/${growoutPreIndividualId}`,
    UPDATE_SAMPLING_20_GR: (growoutPreIndividualId: string | number, samplingId: string | number) =>
      `/genetic-20-gr-sampling/growout-pre-individual/${growoutPreIndividualId}/${samplingId}`,
    EXPORT_SAMPLING_20_GR: (growoutPreIndividualId: string | number) =>
      `/genetic-20-gr-sampling/growout-pre-individual/${growoutPreIndividualId}/export`,
  },
  GENETIC_20_GR_SAMPLING: {
    BASE: "/genetic-20-gr-sampling",
    GROWOUT_PRE_INDIVIDUAL: (growoutPreIndividualId: string | number) =>
      `/genetic-20-gr-sampling/growout-pre-individual/${growoutPreIndividualId}`,
    UPDATE: (growoutPreIndividualId: string | number, samplingId: string | number) =>
      `/genetic-20-gr-sampling/growout-pre-individual/${growoutPreIndividualId}/${samplingId}`,
    DETAIL: (growoutPreIndividualId: string | number, samplingId: string | number) =>
      `/genetic-20-gr-sampling/growout-pre-individual/${growoutPreIndividualId}/${samplingId}`,
    UNIT_DETAIL: (growoutPreIndividualId: string | number) =>
      `/genetic-20-gr-sampling/growout-pre-individual/${growoutPreIndividualId}/detail`,
    EXPORT: (growoutPreIndividualId: string | number) =>
      `/genetic-20-gr-sampling/growout-pre-individual/${growoutPreIndividualId}/export`,
    EXPORT_DETAIL: (growoutPreIndividualId: string | number) =>
      `/genetic-20-gr-sampling/growout-pre-individual/${growoutPreIndividualId}/export-detail`,
    CALCULATE_ADG: (growoutPreIndividualId: string | number) =>
      `/genetic-20-gr-sampling/growout-pre-individual/${growoutPreIndividualId}/calculate-adg`,
    PIT_TAGS: (growoutPreIndividualId: string | number) =>
      `/genetic-20-gr-sampling/growout-pre-individual/${growoutPreIndividualId}/pit-tags`,
  },
  SCHEDULE_TEMPLATES: {
    BASE: "/schedule-template",
    BY_ID: (id: string | number) => `/schedule-template/${id}`,
    UPDATE: (id: string | number) => `/schedule-template/${id}`,
    DEACTIVATE: (id: string | number) => `/schedule-template/${id}/deactivate`,
    ACTIVATE: (id: string | number) => `/schedule-template/${id}/activate`,
  },
  FEEDING_TYPE: "/feeding-type",
  TASK_REDELEGATE: {
    LIST: "/task-redelegate",
    USER_TASK: "/task-redelegate/user-task",
    USER: "/task-redelegate/user",
    DELETE: (id: string | number) => `/task-redelegate/${id}`,
  },
  MY_TASKS: {
    BASE: "/my-task",
    FEEDING: "/my-task/feeding",
    MONITORING: "/my-task/monitoring",
    TEMPERATURE: "/my-task/temperature",
    DRAFT: "/my-task/draft",
  },
  SCHEDULE_TASK: {
    LIST: "/schedule-task",
    DETAIL: (id: string | number) => `/schedule-task/${id}`,
    CREATE: "/schedule-task",
    UPDATE: (id: string | number) => `/schedule-task/${id}`,
    DEACTIVATE: (id: string | number) => `/schedule-task/${id}/deactivate`,
    ACTIVATE: (id: string | number) => `/schedule-task/${id}/activate`,
    REACTIVATE: (id: string | number) => `/schedule-task/${id}/re-activate`,
    USER: "/schedule-task/user",
    TEMPLATE_DETAIL: (id: string | number) => `/schedule-task/template/${id}`,
  },
  REPORTS: {
    MATING_TOTAL_AND_MORTALITY: "/breeding-maturation-report/mating-total-and-mortality",
    MATING_TOTAL_AND_MORTALITY_EXPORT: "/breeding-maturation-report/mating-total-and-mortality/export",
    MATING_PER_TANK: "/breeding-maturation-report/mating-per-tank",
    MATING_PER_TANK_EXPORT: "/breeding-maturation-report/mating-per-tank/export",
    TOTAL_AVG_MATING: (programId: string | number) => `/breeding-maturation-report/total-avg-mating/${programId}`,
    AVG_EGG_NAUPLII: "/breeding-hatching-report/avg-egg-nauplii",
    AVG_FERTIL_HATCHING: "/breeding-hatching-report/avg-fertil-hatching-percentage",
    AVG_PL10_POPULATION: "/breeding-larvae-report/avg-pl10-population",
    POPULATION_PL4_ESTIMATE: "/breeding-larvae-report/population-and-sr/pl4-estimate",
    POPULATION_PL10_ESTIMATE: "/breeding-larvae-report/population-and-sr/pl10-estimate",
    POPULATION_PL10_ACTUAL: "/breeding-larvae-report/population-and-sr/pl10-actual",
    PL_TRIAL_BATCH: "/breeding-maturation-report/pl-trial-batch",
    PL_TRIAL_BATCH_EXPORT: "/breeding-maturation-report/pl-trial-batch/export",
    FEEDING_DAILY: "/breeding-maturation-report/feeding-daily",
    FEEDING_DAILY_EXPORT: "/breeding-maturation-report/feeding-daily/export",
    LARVAE_TANK_DROPDOWN: (batchId: string | number) =>
      `/breeding-larvae-report/larvae-data/${batchId}`,
    LARVAE_TEMPERATURE: (dataId: string | number) =>
      `/breeding-larvae-report/larvae-temperature/${dataId}`,
  },
  GROWOUT_REPORTS: {
    AVG_ADG: "/breeding-growout-report/average-adg",
    AVG_WEIGHT: "/breeding-growout-report/average-weight",
  },
  POND_TRIALS: {
    BASE: "/pondtrial",
    BY_ID: (id: string | number) => `/pondtrial/${id}`,
    AVAILABLE_BATCHES: "/pondtrial/available-batch",
    COLOR_TAG: (id: string | number) => `/pondtrial/${id}/color-tag`,
    COLOR_TAG_EXPORT: (id: string | number) => `/pondtrial/${id}/color-tag/export`,
    WEIGHT_STOCKING: (id: string | number) => `/pondtrial/${id}/weight-stocking`,
    WEIGHT_STOCKING_PIC: (id: string | number, clientId: string | number) => `/pondtrial/${id}/weight-stocking/${clientId}`,
    WEIGHT_STOCKING_DATA: (id: string | number, clientId: string | number) => `/pondtrial/${id}/weight-stocking/${clientId}/data`,
    WEIGHT_STOCKING_EXPORT: (id: string | number, clientId: string | number) => `/pondtrial/${id}/weight-stocking/${clientId}/data/export`,
    MONITORING: (id: string | number) => `/pondtrial/${id}/monitoring`,
    MONITORING_SAMPLING: (trialId: string | number, monitoringId: string | number) => `/pondtrial/${trialId}/monitoring/${monitoringId}/sampling`,
    MONITORING_DATA: (trialId: string | number, monitoringId: string | number) => `/pondtrial/${trialId}/monitoring/${monitoringId}/data`,
    MONITORING_DETAIL: (trialId: string | number, monitoringId: string | number) => `/pondtrial/${trialId}/monitoring/${monitoringId}/detail`,
    MONITORING_EXPORT: (trialId: string | number, monitoringId: string | number) => `/pondtrial/${trialId}/monitoring/${monitoringId}/data/export`,
    MORTALITY: (id: string | number) => `/pondtrial/${id}/mortality`,
    MORTALITY_EXPORT: (id: string | number) => `/pondtrial/${id}/mortality/export`,
    MORTALITY_HISTORY: (id: string | number) => `/pondtrial/${id}/mortality/history`,
    MORTALITY_HISTORY_EXPORT: (id: string | number) => `/pondtrial/${id}/mortality/history/export`,
    HARVEST: (id: string | number) => `/pondtrial/${id}/harvest`,
    HARVEST_DETAIL: (trialId: string | number, clientId: string | number) => `/pondtrial/${trialId}/harvest/${clientId}/detail`,
    HARVEST_DATA: (trialId: string | number, clientId: string | number) => `/pondtrial/${trialId}/harvest/${clientId}/data`,
    HARVEST_DATA_BY_ID: (trialId: string | number, clientId: string | number, id: string | number) => `/pondtrial/${trialId}/harvest/${clientId}/data/${id}`,
    HARVEST_DATA_EXPORT: (trialId: string | number, clientId: string | number) => `/pondtrial/${trialId}/harvest/${clientId}/data/export`,
    DROPDOWN: "/pondtrial/dropdown",
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    CLIENT_DROPDOWN: (_id?: string | number) => `/client/dropdown`,
    WEIGHT_SR_REPORT: "/pondtrial/weight-sr/report",
    WEIGHT_SR_REPORT_EXPORT: "/pondtrial/weight-sr/report/export",
    CAGE_DEFORMITIES_REPORT: "/pondtrial/cage-deformities/report",
    CAGE_DEFORMITIES_REPORT_EXPORT: "/pondtrial/cage-deformities/report/export",
    NBC_DEFORMITIES_REPORT: "/pondtrial/nbc-deformities/report",
    NBC_DEFORMITIES_REPORT_EXPORT: "/pondtrial/nbc-deformities/report/export",
    TAGGING_SHIPPING_REPORT: "/pondtrial/tagging-shipping/report",
    TAGGING_SHIPPING_REPORT_EXPORT: "/pondtrial/tagging-shipping/report/export",
    ALL_CAGES_DEFORMITIES_REPORT: "/pondtrial/all-cage-deformities/report",
    ALL_CAGES_DEFORMITIES_REPORT_EXPORT: "/pondtrial/all-cage-deformities/report/export",
  },
  CAGE_TRIALS: {
    BASE: "/cage-trial",
    BY_ID: (id: string | number) => `/cage-trial/${id}`,
    OVERVIEW: (id: string | number) => `/cage-trial/${id}/overview`,
    AVAILABLE_BATCHES: "/cage-trial/available-batch",
    COLOR_TAG: (id: string | number) => `/cage-trial/${id}/color-tag`,
    COLOR_TAG_EXPORT: (id: string | number) => `/cage-trial/${id}/color-tag/export`,
    WEIGHT_STOCKING: (id: string | number) => `/cage-trial/${id}/weight-stocking`,
    WEIGHT_STOCKING_PIC: (id: string | number, clientId: string | number) => `/cage-trial/${id}/weight-stocking/${clientId}`,
    WEIGHT_STOCKING_DATA: (id: string | number, clientId: string | number) => `/cage-trial/${id}/weight-stocking/${clientId}/data`,
    WEIGHT_STOCKING_EXPORT: (id: string | number, clientId: string | number) => `/cage-trial/${id}/weight-stocking/${clientId}/data/export`,
    MONITORING: (id: string | number) => `/cage-trial/${id}/monitoring`,
    MONITORING_SAMPLING: (trialId: string | number, monitoringId: string | number) => `/cage-trial/${trialId}/monitoring/${monitoringId}/sampling`,
    MONITORING_SAMPLING_CALCULATE_ADG: (trialId: string | number, monitoringId: string | number) => `/cage-trial/${trialId}/monitoring/${monitoringId}/sampling/calculate-adg`,
    MONITORING_SAMPLING_UPDATE: (trialId: string | number, monitoringId: string | number, samplingId: string | number) => `/cage-trial/${trialId}/monitoring/${monitoringId}/sampling/${samplingId}`,
    MONITORING_DATA: (trialId: string | number, monitoringId: string | number) => `/cage-trial/${trialId}/monitoring/${monitoringId}/data`,
    MONITORING_DETAIL: (trialId: string | number, monitoringId: string | number) => `/cage-trial/${trialId}/monitoring/${monitoringId}/detail`,
    MONITORING_EXPORT: (trialId: string | number, monitoringId: string | number) => `/cage-trial/${trialId}/monitoring/${monitoringId}/data/export`,
    MORTALITY: (id: string | number) => `/cage-trial/${id}/mortality`,
    MORTALITY_ANONIM: (id: string | number) => `/cage-trial/${id}/mortality/anonim`,
    MORTALITY_EXPORT: (id: string | number) => `/cage-trial/${id}/mortality/export`,
    MORTALITY_HISTORY: (id: string | number) => `/cage-trial/${id}/mortality/history`,
    MORTALITY_HISTORY_EXPORT: (id: string | number) => `/cage-trial/${id}/mortality/history/export`,
    HARVEST: (id: string | number) => `/cage-trial/${id}/harvest`,
    HARVEST_DETAIL: (trialId: string | number, clientId: string | number) => `/cage-trial/${trialId}/harvest/${clientId}/detail`,
    HARVEST_DATA: (trialId: string | number, clientId: string | number) => `/cage-trial/${trialId}/harvest/${clientId}/data`,
    HARVEST_DATA_BY_ID: (trialId: string | number, clientId: string | number, id: string | number) => `/cage-trial/${trialId}/harvest/${clientId}/data/${id}`,
    HARVEST_DATA_EXPORT: (trialId: string | number, clientId: string | number) => `/cage-trial/${trialId}/harvest/${clientId}/export`,
    HARVEST_VALIDATE_ID_TEST_TUBE: (cagetrialId: string | number) => `/cage-trial/${cagetrialId}/harvest/validate-id-test-tube`,
    DROPDOWN: "/cage-trial/dropdown",
    CLIENT_DROPDOWN: (id: string | number) => `/cage-trial/${id}/client/dropdown`,
    WEIGHT_SR_REPORT: "/cage-trial/weight-sr/report",
    WEIGHT_SR_REPORT_EXPORT: "/cage-trial/weight-sr/report/export",
    CAGE_DEFORMITIES_REPORT: "/cage-trial/cage-deformities/report",
    CAGE_DEFORMITIES_REPORT_EXPORT: "/cage-trial/cage-deformities/report/export",
    NBC_DEFORMITIES_REPORT: "/cage-trial/nbc-deformities/report",
    NBC_DEFORMITIES_REPORT_EXPORT: "/cage-trial/nbc-deformities/report/export",
    TAGGING_SHIPPING_REPORT: "/cage-trial/tagging-shipping/report",
    TAGGING_SHIPPING_REPORT_EXPORT: "/cage-trial/tagging-shipping/report/export",
    ALL_CAGES_DEFORMITIES_REPORT: "/cage-trial/all-cage-deformities/report",
    ALL_CAGES_DEFORMITIES_REPORT_EXPORT: "/cage-trial/all-cage-deformities/report/export",
    METABASE_DASHBOARD: "/metabase/dashboard/cagetrial",
  },
  PRODUCTION_CYCLES: {
    BASE: "/production/cycle",
    BY_ID: (id: string | number) => `/production/cycle/${id}`,
    DATA: (id: string | number) => `/production/cycle/${id}/data`,
    OVERVIEW: (id: string | number) => `/production/cycle/${id}/overview`,
    SUMMARY: (id: string | number) => `/production/cycle/${id}/summary`,
    START: (id: string | number) => `/production/cycle/${id}/start`,
    COMPANY: (companyId: string | number) => `/production/cycle/company/${companyId}`,
    RECEIVING_TANK: (id: string | number) => `/production/cycle/${id}/receiving-tank`,
    RECEIVING_TANK_WEIGHT_AND_BIOMASS: (cycleId: string | number) =>
      `/production/cycle/${cycleId}/receiving-tank/weight-and-biomass`,
    MATURATION_WEIGHT_AND_BIOMASS: (cycleId: string | number) =>
      `/production/cycle/${cycleId}/maturation/weight-and-biomass`,
    RECEIVING_TANK_MORTALITY: (cycleId: string | number) => `/production/cycle/${cycleId}/receiving-tank/mortality`,
    RECEIVING_TANK_MORTALITY_DETAIL: (cycleId: string | number, receivingTankId: string | number) =>
      `/production/cycle/${cycleId}/receiving-tank/mortality/${receivingTankId}`,
    RECEIVING_TANK_MORTALITY_EXPORT: (cycleId: string | number) =>
      `/production/cycle/${cycleId}/receiving-tank/mortality/export`,
    MATURATION_TANK_MORTALITY: (cycleId: string | number) =>
      `/production/cycle/${cycleId}/maturation-tank/mortality`,
    MATURATION_TANK_MORTALITY_DETAIL: (
      cycleId: string | number,
      maturationTankId: string | number,
    ) => `/production/cycle/${cycleId}/maturation-tank/mortality/${maturationTankId}`,
    MATURATION_TANK_MORTALITY_EXPORT: (cycleId: string | number) =>
      `/production/cycle/${cycleId}/maturation-tank/mortality/export`,
    MATURATION_TANK_MORTALITY_DELETE: (
      cycleId: string | number,
      mortalityId: string | number,
    ) => `/production/cycle/${cycleId}/maturation-tank/mortality/${mortalityId}`,
    RECEIVING_TANK_FEEDING: (cycleId: string | number) =>
      `/production/cycle/${cycleId}/receiving-tank/feeding`,
    RECEIVING_TANK_FEEDING_DELETE: (
      cycleId: string | number,
      feedingId: string | number,
    ) => `/production/cycle/${cycleId}/receiving-tank/feeding/${feedingId}`,
    RECEIVING_TANK_FEEDING_EXPORT: (cycleId: string | number) =>
      `/production/cycle/${cycleId}/receiving-tank/feeding/export`,
    RECEIVING_TANK_FEEDING_PREPARATION: (cycleId: string | number) =>
      `/production/cycle/${cycleId}/receiving-tank/feeding/preparation`,
    MATURATION_FEEDING: (cycleId: string | number) =>
      `/production/cycle/${cycleId}/maturation/feeding`,
    MATURATION_FEEDING_PREPARATION: (cycleId: string | number) =>
      `/production/cycle/${cycleId}/maturation/feeding/preparation`,
    MATURATION_FEEDING_EXPORT: (cycleId: string | number) =>
      `/production/cycle/${cycleId}/maturation/feeding/export`,
    MATURATION_FEEDING_DELETE: (
      cycleId: string | number,
      feedingId: string | number,
    ) => `/production/cycle/${cycleId}/maturation/feeding/${feedingId}`,
    RECEIVING_TANK_TEMPERATURE: (cycleId: string | number) =>
      `/production/cycle/${cycleId}/receiving-tank/temperature`,
    RECEIVING_TANK_TEMPERATURE_DELETE: (
      cycleId: string | number,
      temperatureId: string | number,
    ) => `/production/cycle/${cycleId}/receiving-tank/temperature/${temperatureId}`,
    RECEIVING_TANK_TEMPERATURE_EXPORT: (cycleId: string | number) =>
      `/production/cycle/${cycleId}/receiving-tank/temperature/export`,
    MATURATION_TEMPERATURE: (cycleId: string | number) =>
      `/production/cycle/${cycleId}/maturation/temperature`,
    MATURATION_TEMPERATURE_DELETE: (
      cycleId: string | number,
      temperatureId: string | number,
    ) => `/production/cycle/${cycleId}/maturation/temperature/${temperatureId}`,
    MATURATION_TEMPERATURE_EXPORT: (cycleId: string | number) =>
      `/production/cycle/${cycleId}/maturation/temperature/export`,
    MATURATION_WATER_QUALITY: (cycleId: string | number) =>
      `/production/cycle/${cycleId}/maturation/water-quality`,
    MATURATION_WATER_QUALITY_DELETE: (
      cycleId: string | number,
      waterQualityId: string | number,
    ) => `/production/cycle/${cycleId}/maturation/water-quality/${waterQualityId}`,
    MATURATION_WATER_QUALITY_EXPORT: (cycleId: string | number) =>
      `/production/cycle/${cycleId}/maturation/water-quality/export`,
    RECEIVING_TANK_WATER_QUALITY: (cycleId: string | number) =>
      `/production/cycle/${cycleId}/receiving-tank/water-quality`,
    RECEIVING_TANK_WATER_QUALITY_DELETE: (
      cycleId: string | number,
      waterQualityId: string | number,
    ) => `/production/cycle/${cycleId}/receiving-tank/water-quality/${waterQualityId}`,
    RECEIVING_TANK_WATER_QUALITY_EXPORT: (cycleId: string | number) =>
      `/production/cycle/${cycleId}/receiving-tank/water-quality/export`,
    MATURATION: (id: string | number) => `/production/cycle/${id}/maturation`,
    TRANSFER_TO_MATURATION: (cycleId: string | number, receivingTankId: string | number) =>
      `/production/cycle/${cycleId}/receiving-tank/${receivingTankId}`,
    DELETE: (id: string | number) => `/production/cycle/${id}`,
  },
  DISEASE_CHALLENGE: {
    BASE: "/disease-challenge",
    DROPDOWN: "/disease-challenge/dropdown",
    BY_ID: (diseaseId: string | number) => `/disease-challenge/${diseaseId}`,
    EXPORT: (diseaseChallengeId: string | number) => `/disease-challenge/${diseaseChallengeId}/export`,
    OVERVIEW: (diseaseId: string | number) => `/disease-challenge/${diseaseId}/overview`,
    START: (diseaseId: string | number) => `/disease-challenge/${diseaseId}/start`,
    END: (diseaseId: string | number) => `/disease-challenge/${diseaseId}/end`,
    AVAILABLE_BATCH: "/disease-challenge/available-batch",
    COLOR_TAG: (diseaseId: string | number) => `/disease-challenge/${diseaseId}/color-tag`,
    COLOR_TAG_EXPORT: (diseaseId: string | number) => `/disease-challenge/${diseaseId}/color-tag/export`,
    WEIGHT_STOCKING: (diseaseId: string | number) => `/disease-challenge/${diseaseId}/weight-stocking`,
    WEIGHT_STOCKING_DETAIL: (diseaseId: string | number, id: string | number) => `/disease-challenge/${diseaseId}/weight-stocking/${id}`,
    WEIGHT_STOCKING_EXPORT: (diseaseId: string | number, id: string | number) => `/disease-challenge/${diseaseId}/weight-stocking/${id}/export`,
    MONITORING: (diseaseId: string | number) => `/disease-challenge/${diseaseId}/monitoring`,
    MONITORING_DETAIL: (diseaseId: string | number, id: string | number) => `/disease-challenge/${diseaseId}/monitoring/${id}`,
    MONITORING_DATA: (diseaseId: string | number, id: string | number) => `/disease-challenge/${diseaseId}/monitoring/${id}/data`,
    MONITORING_EXPORT: (diseaseId: string | number, id: string | number) => `/disease-challenge/${diseaseId}/monitoring/${id}/data/export`,
    MONITORING_SAMPLING_ADD: (diseaseId: string | number) => `/disease-challenge/${diseaseId}/monitoring/sampling`,
    MONITORING_SAMPLING_CALCULATE_ADG: (diseaseId: string | number, monitoringId: string | number) => `/disease-challenge/${diseaseId}/monitoring/${monitoringId}/sampling/calculate-adg`,
    MONITORING_SAMPLING_UPDATE: (diseaseId: string | number, monitoringId: string | number, samplingId: string | number) => `/disease-challenge/${diseaseId}/monitoring/${monitoringId}/sampling/${samplingId}`,
    MORTALITY: (diseaseId: string | number) => `/disease-challenge/${diseaseId}/mortality`,
    MORTALITY_ANONIM: (diseaseId: string | number) => `/disease-challenge/${diseaseId}/mortality/anonim`,
    MORTALITY_EXPORT: (diseaseId: string | number) => `/disease-challenge/${diseaseId}/mortality/export`,
    MORTALITY_HISTORY: (diseaseId: string | number) => `/disease-challenge/${diseaseId}/mortality/history`,
    MORTALITY_HISTORY_EXPORT: (diseaseId: string | number) => `/disease-challenge/${diseaseId}/mortality/history/export`,
    HARVEST: (diseaseId: string | number) => `/disease-challenge/${diseaseId}/harvest`,
    HARVEST_EXPORT: (diseaseId: string | number) => `/disease-challenge/${diseaseId}/harvest/export`,
    HARVEST_DETAIL: (diseaseId: string | number, id: string | number) => `/disease-challenge/${diseaseId}/harvest/${id}`,
    HARVEST_DETAIL_EXPORT: (diseaseId: string | number, id: string | number) => `/disease-challenge/${diseaseId}/harvest/${id}/export`,
    HARVEST_VALIDATE_ID_TEST_TUBE: (diseaseId: string | number) => `/disease-challenge/${diseaseId}/harvest/validate-id-test-tube`,
    WEIGHT_SR_REPORT: "/disease-challenge/weight-sr/report",
    WEIGHT_SR_REPORT_EXPORT: "/disease-challenge/weight-sr/report/export",
    CAGE_DEFORMITIES_REPORT: "/disease-challenge/cage-deformities/report",
    CAGE_DEFORMITIES_REPORT_EXPORT: "/disease-challenge/cage-deformities/report/export",
    ALL_CAGE_DEFORMITIES_REPORT: "/disease-challenge/all-cage-deformities/report",
    ALL_CAGE_DEFORMITIES_REPORT_EXPORT: "/disease-challenge/all-cage-deformities/report/export",
    METABASE_DASHBOARD: "/metabase/dashboard/disease-challenge",
  },
  PL_TRIAL: {
    BASE: "/pl-trial",
    BY_ID: (id: string | number) => `/pl-trial/${id}`,
    OVERVIEW: (id: string | number) => `/pl-trial/${id}/overview`,
    UPDATE: (id: string | number) => `/pl-trial/${id}`,
    AVAILABLE_BATCHES: "/pl-trial/available-batch",
    STOCKING: (id: string | number) => `/pl-trial/${id}/stocking`,
    UPDATE_STOCKING: (id: string | number, clientId: string | number) =>
      `/pl-trial/${id}/stocking/${clientId}`,
    SAMPLING: (id: string | number) => `/pl-trial/${id}/sampling`,
    SAMPLING_DETAIL: (id: string | number, clientId: string | number) =>
      `/pl-trial/${id}/sampling/${clientId}`,
    ADD_SAMPLING: (plTrialId: string | number, clientId: string | number) =>
      `/pl-trial/${plTrialId}/sampling/${clientId}`,
    SAMPLING_DATA: (id: string | number, clientId: string | number) =>
      `/pl-trial/${id}/sampling/${clientId}/data`,
    UPDATE_SAMPLING_DATA: (
      plTrialId: string | number,
      clientId: string | number,
      id: string | number,
    ) => `/pl-trial/${plTrialId}/sampling/${clientId}/${id}`,
    PARTIAL_HARVEST: (id: string | number) => `/pl-trial/${id}/partial-harvest`,
    PARTIAL_HARVEST_DETAIL: (id: string | number, clientId: string | number) =>
      `/pl-trial/${id}/partial-harvest/${clientId}/detail`,
    PARTIAL_HARVEST_DATA: (id: string | number, clientId: string | number) =>
      `/pl-trial/${id}/partial-harvest/${clientId}/data`,
    UPDATE_PARTIAL_HARVEST_DATA: (
      plTrialId: string | number,
      clientId: string | number,
      id: string | number,
    ) => `/pl-trial/${plTrialId}/partial-harvest/${clientId}/data/${id}`,
    HARVEST: (id: string | number) => `/pl-trial/${id}/harvest`,
    HARVEST_DETAIL: (id: string | number, clientId: string | number) =>
      `/pl-trial/${id}/harvest/${clientId}`,
    UPDATE_HARVEST: (id: string | number, clientId: string | number) =>
      `/pl-trial/${id}/harvest/${clientId}`,
    UPDATE_HARVEST_DATA: (id: string | number, clientId: string | number) =>
      `/pl-trial/${id}/harvest/${clientId}/data`,
    HARVEST_EXPORT: (id: string | number, clientId: string | number) =>
      `/pl-trial/${id}/harvest/${clientId}/export`,
    HARVEST_VALIDATE_SAMPLE_ID: (plTrialId: string | number) =>
      `/pl-trial/${plTrialId}/harvest/validate-sample-id`,
    METABASE_DASHBOARD: "/metabase/dashboard/pl-trial",
  },
} as const;

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
} as const;

export type ApiEndpoints = typeof API_ENDPOINTS;
export type HttpStatus = typeof HTTP_STATUS;
